import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import _sortBy from 'lodash/sortBy';

// Components
import Heading from '../Heading';
import QuantityControl from './partials/QuantityControl';
import Amount from '../Amount';
import VariationInfo from '../VariationInfo';
import AppLink from '../AppLink';
import ToggleArrow from '../ToggleArrow';
import Collapse from '../Collapse';

// Partials
import Sauces from './partials/Sauces';
import AddedSauce from './partials/AddedSauce';
import AddedIngredients from '../AddedIngredients';
import RemovedIngredients from '../RemovedIngredients';
import Gift from './partials/Gift';

// State
import { removeItem, updateItem } from '../../state/modules/cart/actions';

// Router
import { paths, getRoute } from '../../entry/routes';

// Utils
import { isBonus, isGift, isCombobox } from '../../utils/catalog';
import isInStopListDecorator from '../../utils/isInStopListDecorator';

// Icons
import RemoveIcon from '../../icons/close.svg';

// Styles
import styles from './CartItemRow.styl';

const cx = classNames.bind(styles);

export default function CartItem(props) {
  const { className, good, editable } = props;
  const {
    count,
    discount_price,
    hash,
    item,
    price,
    type,
    old_price,
    updating,
    removing,
    deposit,
    deposit_type = '',
  } = good;
  const {
    combo_box_items = [],
    added_ingredients = [],
    added_sauces = [],
    custom_name,
    free_sauce_count = 0,
    image,
    name,
    removed_ingredients = [],
    sauces = [],
  } = item;

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const stopList = useSelector(state => state.restaurant.stopList);
  const unit = useSelector(state => state.city.userCity.currency?.unit);

  const [isCompositionOpen, setIsCompositionOpen] = useState(false);

  const isItGift = isGift(good);
  const isItBonus = isBonus(good);
  const isItCombobox = isCombobox(good);
  const isItBox = good?.type === 'pizza_box';

  const handleRemoveItem = () => {
    return dispatch(removeItem(hash));
  };

  const changeQuantity = quantity => {
    const saucesIds = added_sauces.map(sauce => sauce.id);

    return dispatch(updateItem({ hash, type, item, sauces: saucesIds, count: quantity }));
  };

  const debouncedChangeQuantity = useCallback(
    _debounce(q => changeQuantity(q), 250),
    [],
  );

  const handleAddFreeSauce = newId => {
    const includeSauces = added_sauces.map(sauce => sauce.id);

    return dispatch(updateItem({ hash, type, item, count, sauces: [newId, ...includeSauces] }));
  };

  const handleRemoveFreeSauce = newId => {
    const removedIdx = added_sauces.findIndex(sauce => sauce.id === newId);
    const saucesIds = added_sauces.filter((_, idx) => idx !== removedIdx).map(sauce => sauce.id);

    return dispatch(updateItem({ hash, type, item, count, sauces: saucesIds }));
  };

  const toggleComboComposition = e => {
    e.preventDefault();

    setIsCompositionOpen(prev => !prev);
  };

  const handleEditCombobox = e => {
    e.preventDefault();

    if (!item?.combo_box_id) {
      return;
    }

    history.push({
      pathname: getRoute('comboboxAlias', item.combo_box_id),
      state: {
        editCombobox: {
          hash,
          combo_box_id: item.combo_box_id,
          combo_box_items,
        },
      },
    });
  };

  const saucesCheckedInStopList = sauces.map(sauce => ({
    ...sauce,
    variations: isInStopListDecorator(stopList, sauce.variations),
  }));

  const addedSaucesCheckedInStopList = isInStopListDecorator(stopList, added_sauces);

  return (
    <div className={cx('CartItem', className)}>
      <div className={cx('CartItem__body')}>
        <figure className={cx('CartItem__image', { CartItem__image_preview: !image })}>
          {image && <img src={image} alt={name} />}
        </figure>
        <div className={cx('CartItem__info')}>
          <Heading level={5} className={cx('CartItem__name')}>
            {custom_name || name}
          </Heading>
          <VariationInfo
            deposit_type={deposit_type}
            {...item}
            className={cx('CartItem__description')}
          />
          {deposit && (
            <div className={cx('CartItem__zzlg')}>
              <div>{intl.formatMessage({ id: 'productcard.deposit' })}</div>
              <div>{deposit}</div>
              <div>{unit}</div>
            </div>
          )}
          {added_ingredients.length > 0 && (
            <AddedIngredients list={added_ingredients} item={item} />
          )}
          {removed_ingredients.length > 0 && <RemovedIngredients list={removed_ingredients} />}
          {isItCombobox && (
            <div className={cx('CartItem__composition-actions')}>
              <AppLink
                href="#"
                className={cx('CartItem__composition-toggle')}
                onClick={toggleComboComposition}
              >
                {intl.formatMessage({
                  id: isCompositionOpen ? 'cart.composition.rollUp' : 'cart.composition.more',
                })}
                <ToggleArrow isOpen={isCompositionOpen} />
              </AppLink>
              {editable && item?.combo_box_id && (
                <AppLink href="#" className={cx('CartItem__edit')} onClick={handleEditCombobox}>
                  {intl.formatMessage({ id: 'link.edit' })}
                </AppLink>
              )}
            </div>
          )}
        </div>
        <div className={cx('CartItem__aside')}>
          {editable && !isItGift && (
            <QuantityControl
              isItBox={isItBox}
              className={cx('CartItem__quantity')}
              quantity={count}
              onChange={debouncedChangeQuantity}
              onRemove={handleRemoveItem}
            />
          )}
          {!editable && count}
          <Amount
            className={cx('CartItem__amount')}
            value={Math.max(old_price || price, price)}
            newValue={discount_price}
            size="s"
            bonus={isItBonus}
          />
        </div>
        {editable && isItGift && <Gift className={cx('CartItem__gift')} count={count} />}
        {editable && !isItGift && !isItBox && (
          <button
            className={cx('CartItem__remove')}
            onClick={handleRemoveItem}
            disabled={updating || removing || !editable}
            type="button"
          >
            <RemoveIcon />
          </button>
        )}
      </div>
      <div className={cx('CartItem__footer')}>
        {addedSaucesCheckedInStopList.length > 0 && (
          <div className={cx('CartItem__sauces')}>
            {addedSaucesCheckedInStopList.map(sauce => (
              <AddedSauce
                key={sauce.hash}
                editable={editable}
                onRemove={handleRemoveFreeSauce}
                sauce={sauce}
              />
            ))}
          </div>
        )}
        {Boolean(free_sauce_count - addedSaucesCheckedInStopList.length) && editable && (
          <Sauces
            className={cx('CartItem__add-sauce')}
            sauces={saucesCheckedInStopList}
            disabled={updating}
            addToCart={handleAddFreeSauce}
          />
        )}
        {isItCombobox && (
          <Collapse isOpen={isCompositionOpen}>
            {_sortBy(combo_box_items, 'category_id').map(comboboxItem => (
              <div key={comboboxItem.id} className={cx('CartItem__composition-item')}>
                <Heading level={5} className={cx('CartItem__composition-name')}>
                  {comboboxItem.custom_name || comboboxItem.name}
                </Heading>
                <VariationInfo
                  deposit_type={deposit_type}
                  {...comboboxItem}
                  className={cx('CartItem__composition-description')}
                />
                {(comboboxItem.added_ingredients || []).length > 0 && (
                  <AddedIngredients list={comboboxItem.added_ingredients} />
                )}
                {(comboboxItem.removed_ingredients || []).length > 0 && (
                  <RemovedIngredients list={comboboxItem.removed_ingredients} />
                )}
              </div>
            ))}
          </Collapse>
        )}
      </div>
      {(updating || removing) && (
        <div className={cx('CartItem__loader')}>
          <div className={cx('CartItem__spinner')} />
        </div>
      )}
    </div>
  );
}

CartItem.defaultProps = {
  className: '',
  editable: true,
};

CartItem.propTypes = {
  className: PropTypes.string,
  good: PropTypes.object.isRequired,
  editable: PropTypes.bool,
};
